import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react'
import {AUTH_TOKEN_KEY, env} from '../../../data/constants'
import {
  ICorrespondenceBackendPagination,
  ICorrespondenceMessage,
  ICorrespondenceThread,
  ICorrespondenceThreadDetail,
  ICreateBroadcastThread,
  ICreateCorrespondenceMessage,
  ICreateCorrespondenceThread,
  IMarkThreadAsReadResponse,
  ISearchCorrespondenceThreads,
  ISearchCorrespondenceThreadsResponse,
  IUnreadCountResponse,
} from './interfaces'

const baseQuery = fetchBaseQuery({
  baseUrl: env.Correspondence,
  prepareHeaders: (headers) => {
    const accessToken = localStorage.getItem(AUTH_TOKEN_KEY)
    if (accessToken) {
      headers.set('Authorization', `Bearer ${accessToken}`)
    }
    return headers
  },
})

const customBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  return baseQuery(args, api, extraOptions)
}

export const CorrespondenceApiSlice = createApi({
  reducerPath: 'correspondenceApi',
  baseQuery: customBaseQuery,
  tagTypes: ['CorrespondenceThreads', 'CorrespondenceUnreadCount'],
  endpoints: (builder) => ({
    searchThreads: builder.query<
      ISearchCorrespondenceThreadsResponse,
      ISearchCorrespondenceThreads | undefined
    >({
      query: (search) => {
        const filters: ISearchCorrespondenceThreads = search ?? {}
        const params = new URLSearchParams()

        if (filters.page) params.append('page', filters.page.toString())
        if (filters.pageSize) params.append('pageSize', filters.pageSize.toString())
        if (filters.financierId) params.append('financierId', filters.financierId)
        if (filters.participantUserId) params.append('participantUserId', filters.participantUserId)
        if (filters.participantRole !== undefined) {
          params.append('participantRole', filters.participantRole.toString())
        }
        if (filters.isArchived !== undefined) {
          params.append('isArchived', filters.isArchived.toString())
        }
        if (filters.status !== undefined) {
          params.append('status', filters.status.toString())
        }
        if (filters.sourceService) params.append('sourceService', filters.sourceService)
        if (filters.relatedEntityType) params.append('relatedEntityType', filters.relatedEntityType)
        if (filters.relatedEntityId) params.append('relatedEntityId', filters.relatedEntityId)

        return {url: '/correspondence/threads', params}
      },
      transformResponse: (raw: {
        data: ICorrespondenceThread[]
        paginationMetadata: ICorrespondenceBackendPagination
      }): ISearchCorrespondenceThreadsResponse => ({
        data: raw.data,
        paginationMetadata: {
          totalCount: raw.paginationMetadata.totalItems,
          page: raw.paginationMetadata.currentPage,
          pageSize: raw.paginationMetadata.pageSize,
          totalPages: raw.paginationMetadata.totalPages,
          hasNextPage: raw.paginationMetadata.hasNextPage,
          hasPreviousPage: raw.paginationMetadata.hasPreviousPage,
        },
      }),
      providesTags: (result) => {
        if (!result) return [{type: 'CorrespondenceThreads', id: 'LIST'}]
        return [
          {type: 'CorrespondenceThreads', id: 'LIST'},
          ...result.data.map((thread) => ({type: 'CorrespondenceThreads' as const, id: thread.id})),
        ]
      },
    }),

    getThreadById: builder.query<ICorrespondenceThreadDetail, string>({
      query: (threadId) => `/correspondence/threads/${threadId}`,
      providesTags: (_result, _error, threadId) => [
        {type: 'CorrespondenceThreads', id: 'LIST'},
        {type: 'CorrespondenceThreads', id: threadId},
      ],
    }),

    getUnreadCount: builder.query<IUnreadCountResponse, void>({
      query: () => '/correspondence/threads/unread-count',
      providesTags: [{type: 'CorrespondenceUnreadCount', id: 'TOTAL'}],
    }),

    startThread: builder.mutation<ICorrespondenceThreadDetail, ICreateCorrespondenceThread>({
      query: (body) => ({
        url: '/correspondence/threads',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result) =>
        result
          ? [
              {type: 'CorrespondenceThreads', id: 'LIST'},
              {type: 'CorrespondenceThreads', id: result.id},
              {type: 'CorrespondenceUnreadCount', id: 'TOTAL'},
            ]
          : [
              {type: 'CorrespondenceThreads', id: 'LIST'},
              {type: 'CorrespondenceUnreadCount', id: 'TOTAL'},
            ],
    }),

    addMessage: builder.mutation<
      ICorrespondenceMessage,
      {threadId: string; body: ICreateCorrespondenceMessage}
    >({
      query: ({threadId, body}) => ({
        url: `/correspondence/threads/${threadId}/messages`,
        method: 'POST',
        body,
      }),
      async onQueryStarted({threadId}, {dispatch, queryFulfilled}) {
        try {
          const {data: newMessage} = await queryFulfilled
          dispatch(
            CorrespondenceApiSlice.util.updateQueryData(
              'getThreadById',
              threadId,
              (draft) => {
                draft.messages.push(newMessage)
                draft.lastMessageAt = newMessage.dateCreated
              }
            )
          )
        } catch {
          // cache update not critical — invalidation below handles it
        }
      },
      invalidatesTags: (_result, _error, {threadId}) => [
        {type: 'CorrespondenceThreads', id: 'LIST'},
        {type: 'CorrespondenceThreads', id: threadId},
      ],
    }),

    markThreadAsRead: builder.mutation<IMarkThreadAsReadResponse, string>({
      query: (threadId) => ({
        url: `/correspondence/threads/${threadId}/mark-as-read`,
        method: 'POST',
      }),
      async onQueryStarted(threadId, {dispatch, queryFulfilled}) {
        const patch = dispatch(
          CorrespondenceApiSlice.util.updateQueryData('getThreadById', threadId, (draft) => {
            draft.messages.forEach((m) => {
              m.isReadByCurrentUser = true
            })
          })
        )
        try {
          await queryFulfilled
          dispatch(CorrespondenceApiSlice.util.invalidateTags([{type: 'CorrespondenceUnreadCount', id: 'TOTAL'}]))
        } catch {
          patch.undo()
        }
      },
      invalidatesTags: (_result, _error, threadId) => [
        {type: 'CorrespondenceThreads', id: 'LIST'},
        {type: 'CorrespondenceThreads', id: threadId},
      ],
    }),

    archiveThread: builder.mutation<void, string>({
      query: (threadId) => ({
        url: `/correspondence/threads/${threadId}/archive`,
        method: 'POST',
      }),
      async onQueryStarted(threadId, {dispatch, queryFulfilled}) {
        const patchList = dispatch(
          CorrespondenceApiSlice.util.updateQueryData(
            'searchThreads',
            {isArchived: false},
            (draft) => {
              const thread = draft.data.find((t) => t.id === threadId)
              if (thread) thread.isArchivedForCurrentUser = true
            }
          )
        )
        const patchDetail = dispatch(
          CorrespondenceApiSlice.util.updateQueryData('getThreadById', threadId, (draft) => {
            draft.isArchivedForCurrentUser = true
          })
        )
        try {
          await queryFulfilled
        } catch {
          patchList.undo()
          patchDetail.undo()
        }
      },
      invalidatesTags: (_result, _error, threadId) => [
        {type: 'CorrespondenceThreads', id: 'LIST'},
        {type: 'CorrespondenceThreads', id: threadId},
      ],
    }),

    restoreThread: builder.mutation<void, string>({
      query: (threadId) => ({
        url: `/correspondence/threads/${threadId}/restore`,
        method: 'POST',
      }),
      async onQueryStarted(threadId, {dispatch, queryFulfilled}) {
        const patchList = dispatch(
          CorrespondenceApiSlice.util.updateQueryData(
            'searchThreads',
            {isArchived: true},
            (draft) => {
              const thread = draft.data.find((t) => t.id === threadId)
              if (thread) thread.isArchivedForCurrentUser = false
            }
          )
        )
        const patchDetail = dispatch(
          CorrespondenceApiSlice.util.updateQueryData('getThreadById', threadId, (draft) => {
            draft.isArchivedForCurrentUser = false
          })
        )
        try {
          await queryFulfilled
        } catch {
          patchList.undo()
          patchDetail.undo()
        }
      },
      invalidatesTags: (_result, _error, threadId) => [
        {type: 'CorrespondenceThreads', id: 'LIST'},
        {type: 'CorrespondenceThreads', id: threadId},
      ],
    }),

    startBroadcast: builder.mutation<ICorrespondenceThreadDetail, ICreateBroadcastThread>({
      query: (body) => ({
        url: '/correspondence/threads/broadcast',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result) =>
        result
          ? [
              {type: 'CorrespondenceThreads', id: 'LIST'},
              {type: 'CorrespondenceThreads', id: result.id},
              {type: 'CorrespondenceUnreadCount', id: 'TOTAL'},
            ]
          : [
              {type: 'CorrespondenceThreads', id: 'LIST'},
              {type: 'CorrespondenceUnreadCount', id: 'TOTAL'},
            ],
    }),
  }),
})

export const {
  useSearchThreadsQuery,
  useLazySearchThreadsQuery,
  useGetThreadByIdQuery,
  useLazyGetThreadByIdQuery,
  useGetUnreadCountQuery,
  useStartThreadMutation,
  useStartBroadcastMutation,
  useAddMessageMutation,
  useMarkThreadAsReadMutation,
  useArchiveThreadMutation,
  useRestoreThreadMutation,
} = CorrespondenceApiSlice
